import React, { useEffect, useState, memo, useMemo, useRef } from 'react';
import { Checkbox, Input, Button, Radio } from '@firesoon/antd';
import { Tree } from '../TreeSelect';
import classnames from 'classnames';
import { FixedSizeList as List, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Filter, FilterProps } from './interface';
import './style.less';

// 默认为根据value的模糊搜索
const onSearch = (value, filter, setCurFilter) => {
  if (value && value !== '') {
    let list = filter.filter((item) => item.text?.toUpperCase().indexOf(value?.toUpperCase()) > -1);
    setCurFilter(list);
  } else {
    setCurFilter(filter);
  }
};

const Index = ({
  selectKeys = [],
  setSelectedKeys,
  confirm,
  filter = [],
  onSearchFilter = onSearch,
  visible,
  multiple = true,
  searchable: propsSearchable,
  defaultExpandedKeys,
  logInfo,
}: FilterProps) => {
  const [searchText, setSearch] = useState('');
  const [curFilter, setCurFilter] = useState([] as Array<Filter>);
  const [hasConfirm, setConfirm] = useState(false);
  const [checkedList, setCheck] = useState((selectKeys || []).map((i) => ({ key: i })) ?? []);
  /** 考虑目前业务场景，多选默认有搜索，单选默认没有搜索 */
  const searchable = propsSearchable ?? multiple;
  /** 搜索时 下拉框滚动到顶部 */
  const scrollRef = useRef<any>(null);
  useEffect(() => {
    // 筛选框打开时清空搜索内容
    if (visible) {
      setSearch('');
      setCurFilter(filter);
      setConfirm(false);
    }
    // 筛选框关闭（按钮以外的方式）时，提交筛选内容
    if (!visible) {
      if (!hasConfirm) {
        setConfirm(true);
        confirm?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const onReset = () => {
    setSelectedKeys?.([]);
    setCheck([]);
    setConfirm(true);
    confirm?.();
  };

  const onConfirm = () => {
    setConfirm(true);
    confirm?.();
  };

  const onChange = (e, value) => {
    if (e.target.checked) {
      setSelectedKeys?.(selectKeys?.concat([value]) ?? []);
    } else {
      setSelectedKeys?.(selectKeys?.filter((item) => item !== value));
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    search(value);
    if (scrollRef.current?.scrollTop) {
      scrollRef.current.scrollTop = 0;
    }
  };

  const search = (value = '') => {
    onSearchFilter(value, filter, setCurFilter);
  };

  const radioChange = (e) => {
    setSelectedKeys?.([e.target.value]);
  };

  const handleCheck = (value, option) => {
    setCheck(option?.checkedNodes);
    setSelectedKeys?.(value);
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const Row: any = memo(({ index, style }: any) => {
    // Data passed to List as "itemData" is available as props.data
    // console.log(data, index, style);
    const item = curFilter?.[index] ?? {};
    return (
      <div style={style} title={String(item.text ?? '')}>
        <Checkbox onChange={(e) => onChange(e, item.value)} checked={selectKeys && selectKeys.indexOf(item.value) > -1}>
          {item.text}
        </Checkbox>
      </div>
    );
  }, areEqual);

  const treeData = useMemo(
    () =>
      curFilter?.map((i) => ({
        ...i,
        key: i.value,
        title: i.text,
        children: i.children?.map((child) => ({ ...child, title: child.text, key: child.value })),
      })),
    [curFilter],
  );

  const filterHeigth = useMemo(() => {
    let height: any = curFilter?.length * 30;
    let treeNodeHeight = 0;
    if (curFilter?.find((i) => i.children?.length)) {
      treeNodeHeight +=
        curFilter?.map((child) => (child?.children?.length ?? 0) + 1)?.reduce((a: any, b) => a + b) * 30;
      height = 'auto';
    }
    return { height, treeNodeHeight };
  }, [curFilter]);

  const MultipleRow = useMemo(() => {
    if (curFilter?.find((i) => i.children?.length)) {
      return (
        <Tree
          treeData={treeData}
          showSearch={searchable}
          treeNodeHeight={filterHeigth?.treeNodeHeight}
          searchPlaceholder="请输入名称"
          onCheck={handleCheck}
          defaultExpandedKeys={defaultExpandedKeys ?? treeData?.map((i) => i.key)}
          checkedList={checkedList}
        />
      );
    }
    return (
      <AutoSizer>
        {({ height, width }) => (
          <List height={height} itemCount={curFilter.length} itemSize={28} width={width} outerRef={scrollRef}>
            {Row}
          </List>
        )}
      </AutoSizer>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curFilter, selectKeys]);

  const SingleRow = useMemo(() => {
    return (
      <AutoSizer>
        {({ height, width }) => (
          <Radio.Group onChange={radioChange} value={selectKeys?.[0]}>
            <List height={height} itemCount={curFilter.length} itemSize={28} width={width} outerRef={scrollRef}>
              {({ index, style }) => {
                const item = curFilter?.[index] ?? {};
                return (
                  <div style={style} title={String(item.text ?? '')}>
                    <Radio value={item.value}>{item.text}</Radio>
                  </div>
                );
              }}
            </List>
          </Radio.Group>
        )}
      </AutoSizer>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curFilter, selectKeys]);

  return (
    <div
      className={classnames({
        'fs-table-filter-search-radio-wrapper': !multiple,
        'fs-table-filter-search-wrapper': multiple,
        'fs-table-filter-search-tree-wrapper': curFilter?.find((i) => i.children?.length),
      })}
      {...logInfo}
    >
      {searchable && !curFilter?.find((i) => i.children?.length) && (
        <div style={{ padding: '8px' }}>
          <Input.Search
            placeholder="请输入名称"
            onChange={(e) => handleSearch(e.target.value)}
            value={searchText}
            allowClear
          />
        </div>
      )}
      <div>
        {curFilter?.length > 0 ? (
          <div className="fs-table-filter-search-checkbox" style={{ height: filterHeigth?.height }}>
            {multiple ? MultipleRow : SingleRow}
          </div>
        ) : (
          <p className="fs-table-filter-search-empty">无搜索结果</p>
        )}
      </div>
      <div className="ant-table-filter-dropdown-btns">
        <Button
          type="link"
          disabled={!selectKeys?.length} // 单选or多选: selectKeys选中值为null或空时置灰重置按钮
          className={classnames('fs-table-filter-search-clear', {
            'fs-table-filter-search-clear-action': selectKeys?.length, // 单选or多选: selectKeys非空激活重置按钮
          })}
          onClick={() => onReset()}
        >
          重置
        </Button>
        <Button type="primary" size="small" className="fs-table-filter-search-confirm" onClick={() => onConfirm()}>
          确认
        </Button>
      </div>
    </div>
  );
};
export default Index;
